export type AggregatorType = 'any' | 'none' | 'all';

export type IfErrorType = 'hide' | 'show';

/**
 * CPK-4974: lê a condição nas respostas de OUTRO questionário da mesma claim. A resposta que vale é a de
 * started_at mais recente, em qualquer status (o mesmo critério com que o questionário reabre). `table` é
 * opcional e só aceita cp_questionnaire_answer.
 */
export interface ConditionLookUp {
  table?: string;
  questionnaire_id: string;
}

export interface ConditionEntry {
  key: string;
  condition: string;
  value: string | boolean;
  if_error?: IfErrorType;
  look_up?: ConditionLookUp;
}

export const CONDITION_PII_FIELDS = ['date_of_birth'];

export const CONDITION_LOOKUP_TABLE = 'cp_questionnaire_answer';

const DATE_COMPARATORS = ['$dateEq', '$dateBefore', '$dateAfter'];

/**
 * =====================================================================
 * DYNAMIC VALUE RESOLUTION
 * =====================================================================
 *
 * Suporta valores dinâmicos baseados no momento atual:
 *
 * Sintaxe:
 *   $now                → Data/hora atual
 *   $today              → Data atual à meia-noite (00:00:00)
 *   $now±Nu             → Agora +/- N unidades
 *   $today±Nu           → Hoje +/- N unidades
 *
 * Unidades (u):
 *   y = years (anos)
 *   M = months (meses)
 *   d = days (dias)
 *   h = hours (horas)
 *   m = minutes (minutos)
 *
 * Exemplos:
 *   $today-18y          → 18 anos atrás (para validar maioridade)
 *   $now+30d            → 30 dias no futuro
 *   $today-2y           → 2 anos atrás
 *   $now-24h            → 24 horas atrás
 */

const DYNAMIC_VALUE_REGEX = /^\$(now|today)([+-]\d+[yMdhm])?$/;

/**
 * Verifica se um valor é dinâmico (começa com $now ou $today)
 */
function isDynamicValue(value: any): boolean {
  if (typeof value !== 'string') return false;
  return DYNAMIC_VALUE_REGEX.test(value);
}

/**
 * Resolve um valor dinâmico para uma Date
 * @param value - String no formato $now, $today, $now-18y, $today+30d, etc.
 * @returns Date resolvida ou null se inválido
 */
function resolveDynamicValue(value: string): Date | null {
  const match = value.match(DYNAMIC_VALUE_REGEX);
  if (!match) return null;

  const [, base, modifier] = match;

  // Criar data base
  let date = new Date();

  // Se for $today, zerar horário para meia-noite
  if (base === 'today') {
    date.setHours(0, 0, 0, 0);
  }

  // Se não tem modificador, retornar data base
  if (!modifier) return date;

  // Parsear modificador: +30d, -18y, etc.
  const operation = modifier[0]; // '+' ou '-'
  const amount = parseInt(modifier.slice(1, -1), 10);
  const unit = modifier.slice(-1);

  if (isNaN(amount)) {
    console.warn('DYNAMIC_VALUE_INVALID_AMOUNT', { value, modifier });
    return null;
  }

  const delta = operation === '+' ? amount : -amount;

  switch (unit) {
    case 'y': // years
      date.setFullYear(date.getFullYear() + delta);
      break;
    case 'M': // months
      date.setMonth(date.getMonth() + delta);
      break;
    case 'd': // days
      date.setDate(date.getDate() + delta);
      break;
    case 'h': // hours
      date.setHours(date.getHours() + delta);
      break;
    case 'm': // minutes
      date.setMinutes(date.getMinutes() + delta);
      break;
    default:
      console.warn('DYNAMIC_VALUE_UNKNOWN_UNIT', { value, unit });
      return null;
  }

  return date;
}

/**
 * Resolve valor - se for dinâmico retorna Date, senão retorna o valor original
 */
function resolveValue(value: any): any {
  if (isDynamicValue(value)) {
    return resolveDynamicValue(value);
  }
  return value;
}

/**
 * Normaliza datas para comparação (aceita ISO string ou yyyy-mm-dd)
 */
function parseDate(value: any): Date | null {
  if (!value) return null;

  // Se já é Date
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  // Se é string (ISO ou yyyy-mm-dd)
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

/**
 * Normaliza uma data para comparação apenas de ano/mês/dia (ignora horário e timezone)
 * Isso garante que "2007-12-07T00:00:00Z" e "2007-12-07T03:00:00Z" sejam consideradas iguais
 *
 * IMPORTANTE: Extrai ano/mês/dia em UTC para consistência entre frontend e backend
 */
function normalizeToDateOnly(date: Date): number {
  // Usar UTC para extrair componentes, garantindo consistência
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  // Criar timestamp normalizado (meia-noite UTC daquele dia)
  return Date.UTC(year, month, day);
}

/**
 * Funções de comparação para várias condições
 */
const comparisonFunctions: { [operator: string]: (a: any, b: any) => boolean } =
  {
    $eq: (a, b) => a === b,
    $dif: (a, b) => a !== b,
    $neq: (a, b) => a != b,
    $includes: (a, b) =>
      Array.isArray(a) ? a.includes(b) : String(a).includes(String(b)),
    $in: (a, b) => Array.isArray(b) && b.includes(a),
    $notIn: (a, b) => Array.isArray(b) && !b.includes(a),
    $contains: (a, b) => typeof a === 'string' && a.includes(b),
    $notContains: (a, b) => !(typeof a === 'string' && a.includes(b)),
    $exists: (a, b) => !!a === b,
    $gt: (a, b) => typeof a === 'number' && typeof b === 'number' && a > b,
    $lt: (a, b) => typeof a === 'number' && typeof b === 'number' && a < b,
    $gte: (a, b) => typeof a === 'number' && typeof b === 'number' && a >= b,
    $lte: (a, b) => typeof a === 'number' && typeof b === 'number' && a <= b,
    $startsWith: (a, b) => typeof a === 'string' && a.startsWith(b),
    $endsWith: (a, b) => typeof a === 'string' && a.endsWith(b),
    $isEmpty: (a, b) => (a === null || a === undefined || a === '') === b,
    $isNotEmpty: (a, b) => (a !== null && a !== undefined && a !== '') === b,

    // Operadores de data (comparam apenas ano/mês/dia, ignorando horário/timezone)
    $dateEq: (a, b) => {
      const dateA = parseDate(a);
      const dateB = parseDate(b);
      if (!dateA || !dateB) return false;
      return normalizeToDateOnly(dateA) === normalizeToDateOnly(dateB);
    },

    $dateBefore: (a, b) => {
      const dateA = parseDate(a);
      const dateB = parseDate(b);
      if (!dateA || !dateB) return false;
      return normalizeToDateOnly(dateA) < normalizeToDateOnly(dateB);
    },

    $dateAfter: (a, b) => {
      const dateA = parseDate(a);
      const dateB = parseDate(b);
      if (!dateA || !dateB) return false;
      return normalizeToDateOnly(dateA) > normalizeToDateOnly(dateB);
    },

    $dateBetween: (a, b) => {
      const dateA = parseDate(a);
      if (!dateA) return false;

      // b deve ser array [startDate, endDate]
      if (!Array.isArray(b) || b.length !== 2) {
        console.warn('$dateBetween requires array with [startDate, endDate]', {
          b,
        });
        return false;
      }

      const startDate = parseDate(b[0]);
      const endDate = parseDate(b[1]);
      if (!startDate || !endDate) return false;

      const normalizedA = normalizeToDateOnly(dateA);
      return normalizedA >= normalizeToDateOnly(startDate) && normalizedA <= normalizeToDateOnly(endDate);
    },
  };

/**
 * Obtém valor de uma estrutura de respostas baseado em key dot-notated
 * Ex: "answers.question_123" → data.answers.question_123.value
 * Ex: "answers.cpf_leak_check.addtional_information.leaked" → data.answers["cpf_leak_check"].addtional_information.leaked
 */
export function getValue(data: any, key: string): any {
  const parts = key.split('.');
  const entity = parts[0];

  if (entity === 'answers') {
    // Se tem apenas 2 partes (answers.FIELD), usa comportamento original (pega .value)
    if (parts.length === 2) {
      const fieldKey = parts[1];
      const answerValue = data[entity]?.[fieldKey];

      // Se for objeto com structure { value, isIgnored }
      if (
        answerValue &&
        typeof answerValue === 'object' &&
        'value' in answerValue
      ) {
        if (answerValue.isIgnored) {
          return '';
        }
        return answerValue.value ?? null;
      }

      // Se for valor direto (answers flat)
      return answerValue ?? null;
    }

    // Se tem 3+ partes (answers.FIELD.NESTED.PATH), navega profundamente
    if (parts.length >= 3) {
      const fieldKey = parts[1];
      const nestedPath = parts.slice(2); // ['addtional_information', 'leaked']

      let current = data[entity]?.[fieldKey];

      if (!current) {
        return null;
      }

      // Navegar pelo caminho nested
      for (const pathPart of nestedPath) {
        current = current?.[pathPart];
        if (current === undefined) {
          return null;
        }
      }

      return current;
    }
  }

  if (entity === 'claim') {
    // Navega caminho arbitrário dentro de data.claim
    // Ex: "claim.attributes.origination.button" → data.claim.attributes.origination.button
    const path = parts.slice(1);
    let current = data['claim'];
    for (const part of path) {
      if (current === null || current === undefined) return null;
      current = current[part];
    }
    return current ?? null;
  }

  if (entity === 'pii') {
    const field = parts[1];
    if (parts.length === 2 && CONDITION_PII_FIELDS.includes(field)) {
      return data?.pii?.[field] ?? null;
    }
    console.warn('CONDITION_EVALUATOR_UNSUPPORTED_PII_FIELD', { key });
    return null;
  }

  // Outros entities podem ser implementados aqui se necessário
  console.warn('CONDITION_EVALUATOR_UNSUPPORTED_ENTITY', { entity, key });
  return null;
}

/**
 * CPK-4974: os dados contra os quais uma condição com look_up é lida — as respostas do outro questionário
 * em `data.lookups[questionnaire_id]`. Sem resposta (ou look_up inválido) = respostas vazias.
 */
function lookUpData(data: any, lookUp: ConditionLookUp): any {
  if (
    !lookUp.questionnaire_id ||
    (lookUp.table && lookUp.table !== CONDITION_LOOKUP_TABLE)
  ) {
    console.warn('CONDITION_EVALUATOR_UNSUPPORTED_LOOKUP', { lookUp });
    return { answers: {} };
  }
  return data?.lookups?.[lookUp.questionnaire_id] ?? { answers: {} };
}

/**
 * Avalia uma condição individual
 */
function evaluateCondition(
  key: string,
  condition: string,
  value: string | boolean,
  data: any,
  ifError?: IfErrorType,
  lookUp?: ConditionLookUp
): boolean {
  const onError = ifError === 'hide';
  let comparedValue = getValue(lookUp ? lookUpData(data, lookUp) : data, key);

  // Se o campo não existe (null/undefined), ignorar a condição (retorna false)
  // Exceto para operadores $exists e $isEmpty que precisam avaliar ausência
  if (comparedValue === null || comparedValue === undefined) {
    if (condition === '$exists' || condition === '$isEmpty') {
      // Esses operadores precisam avaliar campos nulos
    } else {
      // Para todos os outros operadores, campo inexistente = condição não ativa
      return onError;
    }
  }

  // Normalizar booleans em strings
  if (comparedValue === 'true') comparedValue = true;
  if (comparedValue === 'false') comparedValue = false;

  // Resolver valor esperado (pode ser dinâmico como $now, $today-18y, etc.)
  let expectedValue: any = resolveValue(value);

  // Normalizar booleans em strings (apenas se não foi resolvido como Date)
  if (typeof expectedValue === 'string') {
    if (expectedValue === 'true') expectedValue = true;
    if (expectedValue === 'false') expectedValue = false;
  }

  const comparisonFn = comparisonFunctions[condition];
  if (!comparisonFn) {
    console.warn('CONDITION_EVALUATOR_UNKNOWN_OPERATOR', { condition, key });
    return false;
  }

  if (
    DATE_COMPARATORS.includes(condition) &&
    (!parseDate(comparedValue) || !parseDate(expectedValue))
  ) {
    return onError;
  }

  return comparisonFn(comparedValue, expectedValue);
}

/**
 * Executa múltiplas condições e agrega resultados
 */
export function runConditions(
  conditioner: {
    aggregator: AggregatorType;
    conditions: ConditionEntry[];
  },
  data: any
): boolean {
  const results: boolean[] = conditioner.conditions.map((cond) =>
    evaluateCondition(
      cond.key,
      cond.condition,
      cond.value,
      data,
      cond.if_error,
      cond.look_up
    )
  );

  switch (conditioner.aggregator) {
    case 'all':
      return !results.includes(false);
    case 'any':
      return results.includes(true);
    case 'none':
      return !results.includes(true);
    default:
      return false;
  }
}

/**
 * Executa múltiplos grupos de condições
 */
export function runConditionsArray(
  conditioners: {
    aggregator: AggregatorType;
    conditions: ConditionEntry[];
  }[],
  data: any
): boolean {
  for (let i = 0; i < conditioners.length; i++) {
    const conditioner = conditioners[i];
    const result = runConditions(conditioner, data);
    if (!result) {
      return false;
    }
  }
  return true;
}
